import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import { AudioPlayer } from '@/features/viewers/Audio';
import { Recording } from '@vivport/backend/types';
import { Check, Copy } from 'lucide-react';
import { useCallback, useState } from 'react';

interface DetailsRecordingProps {
  content: Recording;
  onDescriptionSave: (description: string) => Promise<void>;
  isRegenerating: boolean;
}

export function DetailsRecording({ content: recording, isRegenerating }: DetailsRecordingProps) {
  // Component state
  const [copiedTranscript, setCopiedTranscript] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);

  // Handle transcript and summary saves
  const handleTranscriptSave = useCallback(
    async (newTranscript: string) => {
      if (!recording) return;
      // Transcript edits are not persisted yet; keep explicit no-op logging until API support exists.
      console.log('Transcript save not yet implemented:', newTranscript);
    },
    [recording]
  );

  const handleSummarySave = useCallback(
    async (newSummary: string) => {
      if (!recording) return;
      // Summary edits are not persisted yet; keep explicit no-op logging until API support exists.
      console.log('Summary save not yet implemented:', newSummary);
    },
    [recording]
  );

  const copyToClipboard = useCallback(async (text: string, type: 'transcript' | 'summary') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'transcript') {
        setCopiedTranscript(true);
        setTimeout(() => setCopiedTranscript(false), 2000);
      } else {
        setCopiedSummary(true);
        setTimeout(() => setCopiedSummary(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, []);

  return (
    <div data-testid="details-recording" className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-4">
        {/* Only show AudioPlayer for non-text recordings */}
        {!recording.uri.startsWith('text-') ? (
          <AudioPlayer src={`/api/files/recording/${recording.uri}`} />
        ) : (
          <div className="w-full px-6 py-3 bg-muted/50 rounded-lg border border-dashed">
            <p className="text-sm text-muted-foreground text-left italic">
              No audio recording saved as text input was preferred upon creation.
            </p>
          </div>
        )}
        {recording.transcript || isRegenerating ? (
          <div className="w-full flex flex-col gap-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Transcript</CardTitle>
                  {recording.transcript && !isRegenerating && (
                    <button
                      onClick={() => copyToClipboard(recording.transcript?.trim() || '', 'transcript')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy transcript to clipboard"
                    >
                      {copiedTranscript ? (
                        <Check className="w-4 h-4 text-success" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                      )}
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ContentDescriptionEditable
                  description={recording.transcript?.trim() || ''}
                  onSave={handleTranscriptSave}
                  disabled={isRegenerating}
                  placeholder={isRegenerating ? "Regeneration currently in progress" : "Click to add transcript"}
                  className="p-0"
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Summary</CardTitle>
                  {recording.description && !isRegenerating && (
                    <button
                      onClick={() => copyToClipboard(recording.description?.trim() || '', 'summary')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy summary to clipboard"
                    >
                      {copiedSummary ? (
                        <Check className="w-4 h-4 text-success" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                      )}
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ContentDescriptionEditable
                  description={recording.description?.trim() || ''}
                  onSave={handleSummarySave}
                  disabled={isRegenerating}
                  placeholder={isRegenerating ? "Regeneration currently in progress" : "Click to add summary"}
                  className="p-0"
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="italic text-muted-foreground text-sm">No transcript found</p>
        )}
      </div>
    </div>
  );
}
